// Everything done with one company, as dated rows, for the Entity History
// page: sales, purchases, payments in and out, credit notes and returns,
// advances, broker commission earned and paid, and service bills (lab,
// shipping, insurance…) and their payment. Plus the stones currently out
// with them on memo.
//
// One shot (not live). Each part loads only if the user may open the page
// it belongs to, so a staff login sees no more here than elsewhere.
// Amounts are in the business's base currency.

import { collection, collectionGroup, getDocs, query, where, type Firestore } from 'firebase/firestore';
import { returnCreditOf } from './tradingSystem';
import type {
  Entity, SaleInvoice, PurchaseInvoice, SalePayment, PurchasePayment, SalesReturn, PurchaseReturn,
  CustomerAdvance, SupplierAdvance, AdvanceApplication, InventoryMemo, InventoryItem, BrokerPayment,
  PurchaseExpense, SaleExpense, Expense, InvoiceAdjustment,
} from '../types';

export type HistoryKind =
  | 'sale' | 'credit' | 'received' | 'adv-in' | 'adv-in-used'
  | 'purchase' | 'return' | 'discount' | 'paid' | 'adv-out' | 'adv-out-used'
  | 'commission' | 'commission-paid'
  | 'bill' | 'bill-paid';

export type HistoryGroup = 'sales' | 'purchases' | 'payments' | 'advances' | 'broker' | 'services';

/** How each kind reads on screen, its colour and which Type filter it falls under. */
export const HISTORY_KIND: Record<HistoryKind, { label: string; color: string; group: HistoryGroup }> = {
  'sale':            { label: 'Sale',             color: 'var(--primary)',       group: 'sales' },
  'credit':          { label: 'Credit note',      color: 'var(--warning)',       group: 'sales' },
  'received':        { label: 'Payment received', color: 'var(--success)',       group: 'payments' },
  'adv-in':          { label: 'Advance received', color: 'var(--success)',       group: 'advances' },
  'adv-in-used':     { label: 'Advance used',     color: 'var(--text-3)',        group: 'advances' },
  'purchase':        { label: 'Purchase',         color: 'var(--accent-violet)', group: 'purchases' },
  'return':          { label: 'Purchase return',  color: 'var(--warning)',       group: 'purchases' },
  'discount':        { label: 'Discount',         color: 'var(--success)',       group: 'purchases' },
  'paid':            { label: 'Payment made',     color: 'var(--danger)',        group: 'payments' },
  'adv-out':         { label: 'Advance paid',     color: 'var(--danger)',        group: 'advances' },
  'adv-out-used':    { label: 'Advance used',     color: 'var(--text-3)',        group: 'advances' },
  'commission':      { label: 'Commission',       color: 'var(--accent-teal)',   group: 'broker' },
  'commission-paid': { label: 'Commission paid',  color: 'var(--danger)',        group: 'broker' },
  'bill':            { label: 'Service bill',     color: 'var(--accent-sky)',    group: 'services' },
  'bill-paid':       { label: 'Bill paid',        color: 'var(--danger)',        group: 'services' },
};

export interface HistoryRow {
  key:    string;
  date:   string;     // YYYY-MM-DD
  kind:   HistoryKind;
  ref:    string;     // invoice / return / advance / bill number
  detail: string;
  carat?: number;
  amount: number;
  /** The sale or purchase invoice this row belongs to, to open it. */
  invoice?: { coll: 'sales' | 'invoices'; id: string };
  /** When the record was saved (ISO), only to order rows on the same day. Never shown. */
  at?: string;
}

/** A doc's saved time as ISO text (createdAt as text, Timestamp or Date), '' when missing. */
export function savedAt(x: unknown): string {
  const v = (x as { createdAt?: unknown } | null)?.createdAt;
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString();
  const t = v as { toDate?: () => Date; seconds?: number };
  if (typeof t.toDate === 'function') return t.toDate().toISOString();
  if (typeof t.seconds === 'number') return new Date(t.seconds * 1000).toISOString();
  return '';
}

/** Newest first: by date, then by the time it was saved, then by key. */
export function compareNewest(a: { date: string; at?: string; key: string }, b: { date: string; at?: string; key: string }): number {
  return b.date.localeCompare(a.date) || (b.at ?? '').localeCompare(a.at ?? '') || a.key.localeCompare(b.key);
}

/** Which money column a kind goes in on screen: what was invoiced / billed (returns and credit notes take it back), or what was paid / received. */
export function historySide(kind: HistoryKind): { col: 'invoiced' | 'paid' | 'none'; sign: 1 | -1 } {
  switch (kind) {
    // Using an advance only moves money already counted when the advance
    // came in (or went out) onto an invoice, so it adds to neither column.
    case 'adv-in-used': case 'adv-out-used':                     return { col: 'none', sign: 1 };
    case 'sale': case 'purchase': case 'commission': case 'bill': return { col: 'invoiced', sign: 1 };
    case 'credit': case 'return': case 'discount':              return { col: 'invoiced', sign: -1 };
    default:                                                     return { col: 'paid', sign: 1 };
  }
}

export interface OpenMemoRow {
  key: string; date: string; code?: string; stone: string; carat: number;
  pricePerCt?: number; value: number; returnBy?: string;
}

export interface EntityHistoryData {
  rows:      HistoryRow[];
  memos:     OpenMemoRow[];
  sales:     SaleInvoice[];
  purchases: PurchaseInvoice[];
  /** Broker: commission still to pay them (earned − paid). */
  commissionDue: number;
  /** Services: their bills not paid yet. */
  billsDue: number;
  /** Customer advances not used yet (their money we hold). */
  advanceHeld: number;
  /** Supplier advances not used yet (our money they hold). */
  advanceGiven: number;
}

export interface HistoryAccess { sales: boolean; purchases: boolean; memos: boolean; brokers: boolean; expenses: boolean }

const round2 = (n: number) => Math.round(n * 100) / 100;
const items = (n: number) => `${n} item${n === 1 ? '' : 's'}`;
const ctText = (n: number) => `${(n ?? 0).toFixed(2)} ct`;
// Plain amount for Details text (the page formats the money columns in the business currency).
const money2 = (n: number) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** What's still owed on an invoice (same rule as AP / AR). */
export const invoiceOutstanding = (i: { totalAmountUSD: number; paidUSD?: number; creditedUSD?: number }) =>
  Math.max(0, (i.totalAmountUSD ?? 0) - (i.paidUSD ?? 0) - (i.creditedUSD ?? 0));

type BrokerLine = {
  amountUSD?: number; grossAmountUSD?: number;
  broker1EntityId?: string; broker1CommissionPercent?: number;
  broker2EntityId?: string; broker2CommissionPercent?: number;
};
/** This broker's commission on one invoice: per-line Broker 1 / 2 (on the full amount), plus a legacy header broker. */
function commissionFor(brokerId: string, lines: BrokerLine[], header?: { brokerEntityId?: string; brokerCommissionUSD?: number }) {
  let sum = 0;
  for (const li of lines ?? []) {
    const base = li.grossAmountUSD ?? li.amountUSD ?? 0;
    if (li.broker1EntityId === brokerId && li.broker1CommissionPercent) sum += round2(base * li.broker1CommissionPercent / 100);
    if (li.broker2EntityId === brokerId && li.broker2CommissionPercent) sum += round2(base * li.broker2CommissionPercent / 100);
  }
  if (header?.brokerEntityId === brokerId && header.brokerCommissionUSD) sum += header.brokerCommissionUSD;
  return round2(sum);
}

export async function loadEntityHistory(db: Firestore, entity: Entity, can: HistoryAccess): Promise<EntityHistoryData> {
  const id = entity.id;
  const rows: HistoryRow[] = [];
  let sales: SaleInvoice[] = [];
  let purchases: PurchaseInvoice[] = [];
  let commissionDue = 0;
  let billsDue = 0;
  let advanceHeld = 0;
  let advanceGiven = 0;

  const tasks: Promise<void>[] = [];

  // ── As a customer ──
  if (can.sales) tasks.push((async () => {
    const [s, r, a] = await Promise.all([
      getDocs(query(collection(db, 'sales'), where('toEntityId', '==', id))),
      getDocs(query(collection(db, 'salesReturns'), where('toEntityId', '==', id))),
      getDocs(query(collection(db, 'customerAdvances'), where('toEntityId', '==', id))),
    ]);
    sales = s.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice));
    for (const inv of sales) {
      rows.push({ key: `s_${inv.id}`, at: savedAt(inv), date: inv.saleDate, kind: 'sale', ref: inv.invoiceNo,
        detail: `${items(inv.lineItems?.length ?? 0)} · ${ctText(inv.totalCarat)}`, carat: inv.totalCarat, amount: inv.totalAmountUSD,
        invoice: { coll: 'sales', id: inv.id } });
    }
    const pays = await Promise.all(sales.map(inv => getDocs(collection(db, 'sales', inv.id, 'payments'))
      .then(ps => ps.docs.map(d => ({ inv, p: { id: d.id, ...d.data() } as SalePayment })))));
    for (const { inv, p } of pays.flat()) {
      rows.push({ key: `sp_${p.id}`, at: savedAt(p), date: p.paidDate, kind: 'received', ref: inv.invoiceNo, amount: p.amount,
        detail: [p.bankAccountName, p.referenceNo].filter(Boolean).join(' · ') || 'Against this sale',
        invoice: { coll: 'sales', id: inv.id } });
    }
    for (const d of r.docs) {
      const x = { id: d.id, ...d.data() } as SalesReturn;
      rows.push({ key: `sr_${x.id}`, at: savedAt(x), date: x.date, kind: 'credit', ref: x.saleInvoiceNo, carat: x.totalCarat,
        amount: returnCreditOf(x.totalRevenueUSD, x), detail: `${x.returnNo} · ${ctText(x.totalCarat)} back`,
        invoice: x.saleId ? { coll: 'sales', id: x.saleId } : undefined });
    }
    const custAdv = a.docs.map(d => ({ id: d.id, ...d.data() } as CustomerAdvance));
    for (const x of custAdv) {
      const left = round2((x.amountUSD ?? 0) - (x.appliedUSD ?? 0));
      advanceHeld += Math.max(0, left);
      rows.push({ key: `ca_${x.id}`, at: savedAt(x), date: x.date, kind: 'adv-in', ref: x.advanceNo, amount: x.amountUSD,
        detail: [x.bankAccountName, left > 0.005 ? `${money2(left)} not used yet` : 'All used'].filter(Boolean).join(' · ') });
    }
    const apps = await Promise.all(custAdv.map(x => getDocs(collection(db, 'customerAdvances', x.id, 'applications'))
      .then(ps => ps.docs.map(d => ({ x, ap: { id: d.id, ...d.data() } as AdvanceApplication })))));
    for (const { x, ap } of apps.flat()) {
      rows.push({ key: `cau_${ap.id}`, at: savedAt(ap), date: ap.date, kind: 'adv-in-used', ref: ap.invoiceNo, amount: ap.amountUSD,
        detail: `${money2(ap.amountUSD)} from advance ${x.advanceNo}`, invoice: { coll: 'sales', id: ap.invoiceId } });
    }
  })());

  // ── As a supplier ──
  if (can.purchases) tasks.push((async () => {
    const [b, r, a] = await Promise.all([
      getDocs(query(collection(db, 'invoices'), where('fromEntityId', '==', id))),
      getDocs(query(collection(db, 'purchaseReturns'), where('fromEntityId', '==', id))),
      getDocs(query(collection(db, 'supplierAdvances'), where('fromEntityId', '==', id))),
    ]);
    purchases = b.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice));
    for (const inv of purchases) {
      rows.push({ key: `p_${inv.id}`, at: savedAt(inv), date: inv.purchaseDate ?? '', kind: 'purchase', ref: inv.invoiceNo,
        detail: [items(inv.lineItems?.length ?? 0), ctText(inv.totalCarat), inv.supplierInvoiceNo ? `Their no ${inv.supplierInvoiceNo}` : ''].filter(Boolean).join(' · '),
        carat: inv.totalCarat, amount: inv.totalAmountUSD, invoice: { coll: 'invoices', id: inv.id } });
    }
    const pays = await Promise.all(purchases.map(inv => getDocs(collection(db, 'invoices', inv.id, 'payments'))
      .then(ps => ps.docs.map(d => ({ inv, p: { id: d.id, ...d.data() } as PurchasePayment })))));
    for (const { inv, p } of pays.flat()) {
      rows.push({ key: `pp_${p.id}`, at: savedAt(p), date: p.paidDate, kind: 'paid', ref: inv.invoiceNo, amount: p.amount,
        detail: [p.bankAccountName, p.referenceNo].filter(Boolean).join(' · ') || 'Against this purchase',
        invoice: { coll: 'invoices', id: inv.id } });
    }
    // Discounts the supplier gave (only invoices that carry one are read).
    const discs = await Promise.all(purchases.filter(inv => (inv.discountUSD ?? 0) > 0.005)
      .map(inv => getDocs(collection(db, 'invoices', inv.id, 'adjustments'))
        .then(ds => ds.docs.map(d => ({ inv, a: { id: d.id, ...d.data() } as InvoiceAdjustment })))));
    for (const { inv, a } of discs.flat()) {
      if (a.kind !== 'discount') continue;
      rows.push({ key: `pd_${a.id}`, at: savedAt(a), date: a.date, kind: 'discount', ref: inv.invoiceNo, amount: a.amountUSD,
        detail: a.reason || 'Discount on this purchase', invoice: { coll: 'invoices', id: inv.id } });
    }
    for (const d of r.docs) {
      const x = { id: d.id, ...d.data() } as PurchaseReturn;
      rows.push({ key: `pr_${x.id}`, at: savedAt(x), date: x.date, kind: 'return', ref: x.invoiceNo, carat: x.totalCarat,
        amount: returnCreditOf(x.totalCostUSD, x), detail: `${x.returnNo} · ${ctText(x.totalCarat)} sent back`,
        invoice: x.invoiceId ? { coll: 'invoices', id: x.invoiceId } : undefined });
    }
    const supAdv = a.docs.map(d => ({ id: d.id, ...d.data() } as SupplierAdvance));
    for (const x of supAdv) {
      const left = round2((x.amountUSD ?? 0) - (x.appliedUSD ?? 0));
      advanceGiven += Math.max(0, left);
      rows.push({ key: `sa_${x.id}`, at: savedAt(x), date: x.date, kind: 'adv-out', ref: x.advanceNo, amount: x.amountUSD,
        detail: [x.bankAccountName, left > 0.005 ? `${money2(left)} not used yet` : 'All used'].filter(Boolean).join(' · ') });
    }
    const apps = await Promise.all(supAdv.map(x => getDocs(collection(db, 'supplierAdvances', x.id, 'applications'))
      .then(ps => ps.docs.map(d => ({ x, ap: { id: d.id, ...d.data() } as AdvanceApplication })))));
    for (const { x, ap } of apps.flat()) {
      rows.push({ key: `sau_${ap.id}`, at: savedAt(ap), date: ap.date, kind: 'adv-out-used', ref: ap.invoiceNo, amount: ap.amountUSD,
        detail: `${money2(ap.amountUSD)} from advance ${x.advanceNo}`, invoice: { coll: 'invoices', id: ap.invoiceId } });
    }
  })());

  // ── As a broker: commission earned on sales and purchases, and paid ──
  // A broker sits on line items, which can't be queried, so every invoice
  // is read and checked here (only for companies marked Broker).
  if (can.brokers && entity.types.includes('broker')) tasks.push((async () => {
    const [allSales, allBuys] = await Promise.all([getDocs(collection(db, 'sales')), getDocs(collection(db, 'invoices'))]);
    const deals: { coll: 'sales' | 'invoices'; id: string; no: string; date: string; at: string; party: string; amount: number; headerBroker: boolean }[] = [];
    for (const d of allSales.docs) {
      const inv = { id: d.id, ...d.data() } as SaleInvoice;
      const amt = commissionFor(id, inv.lineItems as BrokerLine[], inv as unknown as { brokerEntityId?: string; brokerCommissionUSD?: number });
      if (amt > 0) deals.push({ coll: 'sales', id: inv.id, no: inv.invoiceNo, date: inv.saleDate, at: savedAt(inv), party: inv.toEntityId, amount: amt,
        headerBroker: (inv as unknown as { brokerEntityId?: string }).brokerEntityId === id });
    }
    for (const d of allBuys.docs) {
      const inv = { id: d.id, ...d.data() } as PurchaseInvoice;
      const amt = commissionFor(id, inv.lineItems as BrokerLine[], inv);
      if (amt > 0) deals.push({ coll: 'invoices', id: inv.id, no: inv.invoiceNo, date: inv.purchaseDate ?? '', at: savedAt(inv), party: inv.fromEntityId, amount: amt,
        headerBroker: inv.brokerEntityId === id });
    }
    let earned = 0, paid = 0;
    for (const deal of deals) {
      earned += deal.amount;
      rows.push({ key: `bc_${deal.coll}_${deal.id}`, at: deal.at, date: deal.date, kind: 'commission', ref: deal.no, amount: deal.amount,
        detail: deal.coll === 'sales' ? 'On a sale' : 'On a purchase', invoice: { coll: deal.coll, id: deal.id } });
    }
    const pays = await Promise.all(deals.map(deal => getDocs(collection(db, deal.coll, deal.id, 'brokerPayments'))
      .then(ps => ps.docs.map(d => ({ deal, p: { id: d.id, ...d.data() } as BrokerPayment })))));
    for (const { deal, p } of pays.flat()) {
      // A payment names its broker; older ones (no name) belong to the invoice's header broker.
      if (p.brokerEntityId ? p.brokerEntityId !== id : !deal.headerBroker) continue;
      paid += p.amount;
      rows.push({ key: `bp_${p.id}`, at: savedAt(p), date: p.paidDate, kind: 'commission-paid', ref: deal.no, amount: p.amount,
        detail: p.bankAccountName ?? '', invoice: { coll: deal.coll, id: deal.id } });
    }
    commissionDue = Math.max(0, round2(earned - paid));
  })());

  // ── As a service (lab, shipping, insurance…): bills against them ──
  if (can.expenses) tasks.push((async () => {
    const [pe, se, ex] = await Promise.all([
      getDocs(query(collection(db, 'purchaseExpenses'), where('vendorEntityId', '==', id))),
      getDocs(query(collection(db, 'saleExpenses'), where('vendorEntityId', '==', id))),
      getDocs(query(collection(db, 'expenses'), where('vendorEntityId', '==', id))),
    ]);
    const bills: { key: string; at: string; no: string; date: string; desc: string; amount: number; paidUSD: number; paidDate?: string }[] = [];
    for (const d of pe.docs) {
      const x = { id: d.id, ...d.data() } as PurchaseExpense;
      bills.push({ key: `pe_${x.id}`, at: savedAt(x), no: x.expenseNo, date: x.date, desc: [x.category, x.description].filter(Boolean).join(' · '), amount: x.amountUSD, paidUSD: x.paidUSD, paidDate: x.paidDate });
    }
    for (const d of se.docs) {
      const x = { id: d.id, ...d.data() } as SaleExpense;
      bills.push({ key: `se_${x.id}`, at: savedAt(x), no: x.expenseNo, date: x.date, desc: x.description, amount: x.amountUSD, paidUSD: x.paidUSD, paidDate: x.paidDate });
    }
    for (const d of ex.docs) {
      const x = { id: d.id, ...d.data() } as Expense;
      const no = (x as unknown as { expenseNo?: string }).expenseNo ?? '';
      bills.push({ key: `ex_${x.id}`, at: savedAt(x), no, date: x.date, desc: x.description, amount: x.amountUSD, paidUSD: x.paidUSD, paidDate: x.paidDate });
    }
    for (const b of bills) {
      rows.push({ key: b.key, at: b.at, date: b.date, kind: 'bill', ref: b.no, amount: b.amount, detail: b.desc });
      if (b.paidUSD > 0) rows.push({ key: `${b.key}_paid`, at: b.at, date: b.paidDate ?? b.date, kind: 'bill-paid', ref: b.no, amount: b.paidUSD, detail: b.desc });
      billsDue += Math.max(0, b.amount - (b.paidUSD ?? 0));
    }
    billsDue = round2(billsDue);
  })());

  // ── Open Memo Out: current memos (read across all stones and filtered
  // here, like the Memo Out page) plus older stones still carrying the
  // legacy single-memo fields ──
  const memos: OpenMemoRow[] = [];
  if (can.memos) tasks.push((async () => {
    const [m, legacy] = await Promise.all([
      getDocs(collectionGroup(db, 'memos')),
      getDocs(query(collection(db, 'inventory'), where('memoToEntityId', '==', id))),
    ]);
    for (const d of m.docs) {
      const x = { id: d.id, ...d.data() } as InventoryMemo;
      if (x.entityId !== id || x.status !== 'active') continue;
      memos.push({ key: `m_${d.ref.path}`, date: x.date, code: x.code, stone: [x.shape, x.color, x.clarity].filter(Boolean).join(' · '),
        carat: x.carat, pricePerCt: x.pricePerCt, value: x.amountUSD ?? (x.pricePerCt ?? 0) * x.carat, returnBy: x.returnByDate });
    }
    for (const d of legacy.docs) {
      const x = { id: d.id, ...d.data() } as InventoryItem & { memoCarat?: number; memoDate?: string; memoReturnByDate?: string; memoPricePerCt?: number };
      if (x.status !== 'on-memo') continue;
      const carat = x.memoCarat ?? x.carat;
      memos.push({ key: `l_${x.id}`, date: x.memoDate ?? '', code: x.code, stone: [x.shape, x.color, x.clarity].filter(Boolean).join(' · '),
        carat, pricePerCt: x.memoPricePerCt, value: (x.memoPricePerCt ?? 0) * carat, returnBy: x.memoReturnByDate });
    }
    memos.sort((a, b) => a.date.localeCompare(b.date));
  })());

  await Promise.all(tasks);
  rows.sort(compareNewest);
  return { rows, memos, sales, purchases, commissionDue, billsDue, advanceHeld: round2(advanceHeld), advanceGiven: round2(advanceGiven) };
}
